import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { 
  MapPin, Search, Trash2, GitMerge, Users, Plus, AlertTriangle, 
  CheckCircle2, History, Copy, Loader2, ArrowRight, RefreshCw,
  ChevronRight, Info, Pencil, ArrowUpDown, ArrowUp, ArrowDown, Filter
} from "lucide-react";

export default function PlaceMaster() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  
  // Sort and filter state
  const [sortBy, setSortBy] = useState("name"); // "name" or "customers"
  const [sortDir, setSortDir] = useState("asc"); // "asc" or "desc"
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  
  // Dialogs
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [customersDialogOpen, setCustomersDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Edit state
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Merge state
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeRemarks, setMergeRemarks] = useState("");
  const [merging, setMerging] = useState(false);
  const [targetSearch, setTargetSearch] = useState("");
  
  // Duplicates
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [threshold, setThreshold] = useState(0.75);
  
  // History
  const [mergeHistory, setMergeHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Customers preview
  const [placeCustomers, setPlaceCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Fetch all places
  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/places/all", { params: { include_inactive: true } });
      setPlaces(data.items || []);
    } catch (e) {
      toast.error("Failed to load places");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  // Fetch duplicates
  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const { data } = await api.get("/places/duplicates", { params: { threshold } });
      setDuplicates(data.groups || []);
    } catch (e) {
      toast.error("Failed to load duplicates");
    } finally {
      setDuplicatesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "duplicates") {
      fetchDuplicates();
    }
  }, [activeTab, threshold]);

  // Filter and sort places
  const filteredPlaces = places
    .filter(p => {
      const matchesSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase());
      if (activeTab === "inactive") {
        return matchesSearch && p.is_active === false;
      }
      // For "all" tab, filter by zero customers if enabled
      const matchesZeroFilter = !showZeroOnly || p.customer_count === 0;
      return matchesSearch && p.is_active !== false && matchesZeroFilter;
    })
    .sort((a, b) => {
      if (sortBy === "customers") {
        const diff = (a.customer_count || 0) - (b.customer_count || 0);
        return sortDir === "asc" ? diff : -diff;
      }
      // Default: sort by name
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      const cmp = nameA.localeCompare(nameB);
      return sortDir === "asc" ? cmp : -cmp;
    });

  // Toggle sort
  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir(column === "customers" ? "desc" : "asc"); // Default to desc for customers
    }
  };

  // Open merge dialog
  const openMergeDialog = (source) => {
    setMergeSource(source);
    setMergeTarget(null);
    setMergePreview(null);
    setMergeRemarks("");
    setTargetSearch("");
    setMergeDialogOpen(true);
  };

  // Fetch merge preview
  const fetchMergePreview = async (targetId) => {
    if (!mergeSource || !targetId) return;
    try {
      const { data } = await api.post("/places/merge/preview", {
        source_place_id: mergeSource.id,
        target_place_id: targetId
      });
      setMergePreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load preview");
      setMergePreview(null);
    }
  };

  // Select target and load preview
  const selectTarget = (target) => {
    setMergeTarget(target);
    fetchMergePreview(target.id);
  };

  // Execute merge
  const executeMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    setMerging(true);
    try {
      const { data } = await api.post("/places/merge", {
        source_place_id: mergeSource.id,
        target_place_id: mergeTarget.id,
        remarks: mergeRemarks
      });
      toast.success(`Merged ${data.customers_updated} customers from "${data.source_place}" to "${data.target_place}"`);
      setMergeDialogOpen(false);
      fetchPlaces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  // Check if place can be deleted
  const openDeleteDialog = async (place) => {
    setSelectedPlace(place);
    setDeleteDialogOpen(true);
  };

  // Execute delete
  const executeDelete = async () => {
    if (!selectedPlace) return;
    try {
      await api.delete(`/places/${selectedPlace.id}`);
      toast.success(`Place "${selectedPlace.name}" deleted`);
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      fetchPlaces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Open edit dialog
  const openEditDialog = (place) => {
    setSelectedPlace(place);
    setEditName(place.name);
    setEditDialogOpen(true);
  };

  // Save edited place name
  const saveEditedPlace = async () => {
    if (!selectedPlace || !editName.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/places/${selectedPlace.id}`, { name: editName.trim() });
      toast.success(data.message || `Place renamed successfully`);
      if (data.customers_updated > 0) {
        toast.info(`${data.customers_updated} customer(s) updated with new place name`);
      }
      setEditDialogOpen(false);
      fetchPlaces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update place");
    } finally {
      setSaving(false);
    }
  };

  // Fetch merge history
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/places/merge/history");
      setMergeHistory(data.items || []);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryDialog = () => {
    setHistoryDialogOpen(true);
    fetchHistory();
  };

  // Fetch place customers
  const openCustomersDialog = async (place) => {
    setSelectedPlace(place);
    setCustomersDialogOpen(true);
    setCustomersLoading(true);
    try {
      const { data } = await api.get(`/places/${place.id}/customers`);
      setPlaceCustomers(data.customers || []);
    } catch (e) {
      toast.error("Failed to load customers");
    } finally {
      setCustomersLoading(false);
    }
  };

  // Filter available targets (exclude source and inactive places)
  const availableTargets = places.filter(p => 
    p.id !== mergeSource?.id && 
    p.is_active !== false &&
    (!targetSearch || p.name?.toLowerCase().includes(targetSearch.toLowerCase()))
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#9E6D5B]" />
            Place Master
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage customer locations with safe merge and duplicate detection
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openHistoryDialog} data-testid="view-history-btn">
            <History className="w-4 h-4 mr-2" />
            Merge History
          </Button>
          <Button variant="outline" onClick={fetchPlaces} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">
            All Places ({places.filter(p => p.is_active !== false).length})
          </TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="tab-duplicates">
            Find Duplicates
          </TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive">
            Merged/Inactive ({places.filter(p => p.is_active === false).length})
          </TabsTrigger>
        </TabsList>

        {/* All Places Tab */}
        <TabsContent value="all" className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search places..."
                className="pl-9"
                data-testid="search-places"
              />
            </div>
            
            {/* Zero Customers Filter */}
            <Button
              variant={showZeroOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowZeroOnly(!showZeroOnly)}
              className={showZeroOnly ? "bg-[#9E6D5B] hover:bg-[#8a5f4f]" : ""}
              data-testid="filter-zero-customers"
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              0 Customers
              {showZeroOnly && (
                <span className="ml-1.5 text-xs bg-white/20 px-1.5 rounded">
                  {places.filter(p => p.is_active !== false && p.customer_count === 0).length}
                </span>
              )}
            </Button>
            
            {/* Results count */}
            <Badge variant="secondary" className="py-1.5 px-3">
              {filteredPlaces.length} places
            </Badge>
          </div>

          {/* Places List View */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        <button 
                          onClick={() => toggleSort("name")}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          data-testid="sort-name"
                        >
                          Place Name
                          {sortBy === "name" ? (
                            sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="text-center p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-32">
                        <button 
                          onClick={() => toggleSort("customers")}
                          className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                          data-testid="sort-customers"
                        >
                          Customers
                          {sortBy === "customers" ? (
                            sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 opacity-40" />
                          )}
                        </button>
                      </th>
                      <th className="text-center p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-28">Status</th>
                      <th className="text-right p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-48">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlaces.map((place, idx) => (
                      <tr 
                        key={place.id} 
                        className={`border-b hover:bg-muted/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                        data-testid={`place-row-${place.id}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-[#9E6D5B] shrink-0" />
                            <span className="font-medium">{place.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <button 
                            onClick={() => openCustomersDialog(place)}
                            className="inline-flex items-center gap-1.5 text-sm hover:text-[#9E6D5B] transition-colors"
                          >
                            <Users className="w-3.5 h-3.5" />
                            <span className="font-mono">{place.customer_count}</span>
                            <ChevronRight className="w-3 h-3 opacity-50" />
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          {place.is_active !== false ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-100 text-gray-500">
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openEditDialog(place)}
                              className="h-8 px-3 text-xs"
                              data-testid={`edit-btn-${place.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1.5" />
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => openMergeDialog(place)}
                              className="h-8 px-3 text-xs"
                              data-testid={`merge-btn-${place.id}`}
                            >
                              <GitMerge className="w-3.5 h-3.5 mr-1.5" />
                              Merge
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPlaces.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No places found
                  </div>
                )}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Copy className="w-5 h-5" />
                Duplicate Detection
              </CardTitle>
              <CardDescription>
                Find similar place names using fuzzy matching. Higher threshold = stricter matching.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <label className="text-sm text-muted-foreground">Similarity Threshold:</label>
                <Input
                  type="number"
                  min="0.5"
                  max="1"
                  step="0.05"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                  className="w-24"
                  data-testid="threshold-input"
                />
                <Button onClick={fetchDuplicates} disabled={duplicatesLoading} data-testid="find-duplicates-btn">
                  {duplicatesLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Find Duplicates
                </Button>
              </div>

              {duplicatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : duplicates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                  <p>No duplicates found at {(threshold * 100).toFixed(0)}% similarity</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Found {duplicates.length} group(s) of similar places
                  </p>
                  {duplicates.map((group, idx) => (
                    <DuplicateGroup 
                      key={idx} 
                      group={group} 
                      onMerge={openMergeDialog}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inactive Tab */}
        <TabsContent value="inactive" className="space-y-4">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search merged places..."
                className="pl-9"
              />
            </div>
          </div>
          
          {filteredPlaces.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No merged/inactive places
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredPlaces.map(place => (
                <Card key={place.id} className="bg-gray-50 opacity-75">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-muted-foreground line-through">
                          {place.name}
                        </div>
                        {place.merged_into_place_name && (
                          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            Merged into: <span className="font-medium">{place.merged_into_place_name}</span>
                          </div>
                        )}
                      </div>
                      <Badge variant="secondary">Inactive</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Merge Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-[#9E6D5B]" />
              Merge Places
            </DialogTitle>
            <DialogDescription>
              Move all customers from source place to destination. Source will become inactive.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4 py-4">
            {/* Source */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="text-xs uppercase tracking-wider text-orange-700 mb-1">Source (will be merged)</div>
              <div className="font-semibold text-lg">{mergeSource?.name}</div>
              <div className="flex items-center gap-2 text-sm text-orange-700 mt-1">
                <Users className="w-4 h-4" />
                {mergeSource?.customer_count || 0} customers
              </div>
            </div>

            {/* Target Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Destination Place:</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={targetSearch}
                  onChange={(e) => setTargetSearch(e.target.value)}
                  placeholder="Search destination place..."
                  className="pl-9"
                  data-testid="merge-target-search"
                />
              </div>
              <ScrollArea className="h-48 border rounded-lg">
                <div className="p-2 space-y-1">
                  {availableTargets.slice(0, 50).map(target => (
                    <button
                      key={target.id}
                      onClick={() => selectTarget(target)}
                      className={`w-full p-2 text-left rounded-md flex items-center justify-between hover:bg-gray-100 ${
                        mergeTarget?.id === target.id ? "bg-emerald-50 border border-emerald-200" : ""
                      }`}
                      data-testid={`merge-target-${target.id}`}
                    >
                      <span className="font-medium">{target.name}</span>
                      <span className="text-xs text-muted-foreground">{target.customer_count} customers</span>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Preview */}
            {mergePreview && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="text-xs uppercase tracking-wider text-emerald-700 mb-2">Merge Preview</div>
                <div className="flex items-center gap-2 text-sm mb-2">
                  <span className="font-medium">{mergePreview.source.name}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-medium">{mergePreview.target.name}</span>
                </div>
                <p className="text-sm text-emerald-700">{mergePreview.summary}</p>
                {mergePreview.sample_customers?.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Sample customers: {mergePreview.sample_customers.map(c => c.name).join(", ")}
                  </div>
                )}
              </div>
            )}

            {/* Remarks */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Remarks (optional):</label>
              <Textarea
                value={mergeRemarks}
                onChange={(e) => setMergeRemarks(e.target.value)}
                placeholder="Reason for merge..."
                rows={2}
                data-testid="merge-remarks"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={executeMerge}
              disabled={!mergeTarget || merging}
              className="bg-[#9E6D5B] hover:bg-[#8a5f4f]"
              data-testid="confirm-merge-btn"
            >
              {merging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GitMerge className="w-4 h-4 mr-2" />}
              Merge {mergePreview?.affected_customers || 0} Customers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {selectedPlace?.customer_count > 0 ? (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              ) : (
                <Trash2 className="w-5 h-5 text-red-500" />
              )}
              Delete Place
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {selectedPlace?.customer_count > 0 ? (
                  <div className="space-y-2">
                    <span className="block">
                      Cannot delete <strong>&ldquo;{selectedPlace?.name}&rdquo;</strong> because it has{" "}
                      <strong>{selectedPlace?.customer_count} customer(s)</strong>.
                    </span>
                    <span className="block text-amber-600">
                      Use <strong>Merge</strong> to move customers to another place first.
                    </span>
                  </div>
                ) : (
                  <span>
                    Are you sure you want to delete <strong>&ldquo;{selectedPlace?.name}&rdquo;</strong>?
                    This action cannot be undone.
                  </span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {selectedPlace?.customer_count === 0 && (
              <AlertDialogAction
                onClick={executeDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            )}
            {selectedPlace?.customer_count > 0 && (
              <Button 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  openMergeDialog(selectedPlace);
                }}
                className="bg-[#9E6D5B] hover:bg-[#8a5f4f]"
              >
                <GitMerge className="w-4 h-4 mr-2" />
                Merge Instead
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Merge History
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : mergeHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No merge history yet
              </div>
            ) : (
              <div className="space-y-3 pr-4">
                {mergeHistory.map(item => (
                  <div key={item.id} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className="line-through text-muted-foreground">{item.source_place_name}</span>
                      <ArrowRight className="w-4 h-4" />
                      <span>{item.target_place_name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                      <span>{item.affected_customer_count} customers moved</span>
                      <span>•</span>
                      <span>by {item.merged_by_name}</span>
                      <span>•</span>
                      <span>{new Date(item.merged_at).toLocaleDateString()}</span>
                    </div>
                    {item.remarks && (
                      <div className="text-xs text-muted-foreground mt-1 italic">
                        &ldquo;{item.remarks}&rdquo;
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Customers Dialog */}
      <Dialog open={customersDialogOpen} onOpenChange={setCustomersDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Customers in &ldquo;{selectedPlace?.name}&rdquo;
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[300px]">
            {customersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : placeCustomers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No customers in this place
              </div>
            ) : (
              <div className="space-y-2 pr-4">
                {placeCustomers.map(cust => (
                  <div key={cust.id} className="p-2 bg-gray-50 rounded border">
                    <div className="font-medium text-sm">{cust.name}</div>
                    <div className="text-xs text-muted-foreground">{cust.phone1}</div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Edit Place Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#9E6D5B]" />
              Edit Place
            </DialogTitle>
            <DialogDescription>
              Rename this place. All {selectedPlace?.customer_count || 0} customer(s) will be updated automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Place Name</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter place name"
                data-testid="edit-place-name"
              />
            </div>
            
            {selectedPlace?.customer_count === 0 && (
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">
                  This place has no customers and can be deleted.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditDialogOpen(false);
                    openDeleteDialog(selectedPlace);
                  }}
                  className="text-red-600 hover:bg-red-50 hover:border-red-200"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Delete Place
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveEditedPlace}
              disabled={saving || !editName.trim() || editName.trim() === selectedPlace?.name}
              className="bg-[#9E6D5B] hover:bg-[#8a5f4f]"
              data-testid="save-place-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Duplicate Group Component
function DuplicateGroup({ group, onMerge }) {
  const primary = group.primary;
  const similar = group.similar || [];
  
  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">
            Potential duplicates ({1 + similar.length} places)
          </span>
        </div>
        
        <div className="space-y-2">
          {/* Primary */}
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div>
              <span className="font-medium">{primary.name}</span>
              <span className="text-xs text-muted-foreground ml-2">
                ({primary.customer_count} customers)
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onMerge(primary)}>
              <GitMerge className="w-3.5 h-3.5 mr-1" />
              Merge
            </Button>
          </div>
          
          {/* Similar */}
          {similar.map((s, idx) => (
            <div key={s.id} className="flex items-center justify-between p-2 bg-white rounded border">
              <div>
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground ml-2">
                  ({s.customer_count} customers)
                </span>
                <Badge variant="outline" className="ml-2 text-xs">
                  {(s.similarity_score * 100).toFixed(0)}% similar
                </Badge>
              </div>
              <Button size="sm" variant="ghost" onClick={() => onMerge(s)}>
                <GitMerge className="w-3.5 h-3.5 mr-1" />
                Merge
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
